import * as type from '../mutation-types';
import { createActionHelpers } from 'vuex-loading';
import Big from 'big.js';

const { startLoading, endLoading } = createActionHelpers({
    moduleName: 'loading',
});

const Laroute = require('../../laroute');

const state = {
    drawerSession: {
        id: null,
        cash_adjustments: [],
        opening_amount: 0,
        closing_amount: 0,
        staff: {
            media_urls: [],
        },
        receipts: [],
        cat_sales: [],
    },
};

const mutations = {
    [type.SET_DRAWER_SESSION](state, drawerSession) {
        state.drawerSession = drawerSession;
    },
};

const getters = {
    hasDrawerSession(state) {
        return state.drawerSession.id !== null;
    },

    drawerSession(state) {
        return state.drawerSession;
    },

    outlet(state) {
        return state.drawerSession.outlet;
    },

    staff(state) {
        return state.drawerSession.staff;
    },

    receipts() {
        return state.drawerSession.receipts;
    },

    totalAverage(state, getters) {
        return getters.netSales / getters.receipts.length;
    },

    staffImage(state, getters) {
        if (getters.staff.media_urls.length)
            return state.drawerSession.staff.media_urls[0].s512;

        return '/img/placeholder-pic.jpg';
    },

    totalPax(state) {
        return state.drawerSession.receipts
            .map(r => {
                const table = r.transaction.order.table;
                return _.isNull(table) ? 0 : table.pax;
            })
            .reduce((a, b) => parseInt(a) + parseInt(b), 0);
    },

    netSales(state, getters) {
        return (
            getters.totalSales -
            (getters.totalTaxes + getters.totalCharges + getters.totalRounding)
        ).toFixed(2);
    },

    catNettSales() {
        return state.drawerSession.cat_sales;
    },

    /** Sum total (RM) of voided orders */
    voidedOrders(state, getters) {
        const voidedOrders = getters.nonVoidedReceipts
            .map(r => {
                return r.transaction.order.products
                    .filter(p => p.voided_at !== null)
                    .map(p => Big(p.unit_price).times(p.quantity));
            })
            .filter(voidedOrders => voidedOrders.length)
            .reduce((a, b) => {
                const voidedAmount = b.reduce((a, b) => Big(a).plus(Big(b)), 0);
                return Big(a).plus(Big(voidedAmount));
            }, 0);

        return Big(voidedOrders).toFixed(2);
    },

    totalVoidedOrders(state, getters) {
        return getters.nonVoidedReceipts
            .map(r => {
                let totalVoided = r.transaction.order.products
                    .filter(p => p.voided_at !== null)
                    .map(p => {
                        return parseInt(p.quantity);
                    })
                    .reduce((a, b) => a + b, 0);

                return totalVoided;
            })
            .reduce((a, b) => a + b, 0);
    },

    cancelledOrders(state, getters) {
        const cancelledOrders = getters.nonVoidedReceipts
            .map(r => {
                return r.transaction.order.products
                    .filter(p => p.cancelled_at !== null)
                    .map(p => Big(p.unit_price).times(p.quantity));
            })
            .filter(cancelledOrders => cancelledOrders.length)
            .reduce((a, b) => {
                const cancelledAmount = b.reduce(
                    (a, b) => Big(a).plus(Big(b)),
                    0,
                );
                return Big(a).plus(Big(cancelledAmount));
            }, 0);

        return Big(cancelledOrders).toFixed(2);
    },

    totalCancelledOrders(state, getters) {
        return getters.nonVoidedReceipts
            .map(r =>
                r.transaction.order.products
                    .filter(p => p.cancelled_at !== null)
                    .map(p => {
                        return parseInt(p.quantity);
                    })
                    .reduce((a, b) => a + b, 0),
            )
            .reduce((a, b) => a + b, 0);
    },

    nonVoidedReceipts(state, getters) {
        return getters.receipts.filter(r => r.voided_at === null);
    },

    voidedReceipts(state, getters) {
        return getters.receipts.filter(r => r.voided_at !== null);
    },

    voidedSales(state, getters) {
        return getters.voidedReceipts
            .map(r => {
                return r.total_amount;
            })
            .reduce((a, b) => parseFloat(a) + parseFloat(b), 0);
    },

    totalVoidedSales(state, getters) {
        return getters.voidedReceipts.length;
    },

    totalSales(state, getters) {
        return getters.nonVoidedReceipts
            .map(r => {
                return r.total_amount;
            })
            .reduce((a, b) => parseFloat(a) + parseFloat(b), 0);
    },

    totalNetSales(state, getters) {
        return getters.catNettSales
            .map(r => {
                return r.total_net_sales;
            })
            .reduce((a, b) => parseFloat(a) + parseFloat(b), 0);
    },

    totalQtyNetSales(state, getters) {
        return getters.catNettSales
            .map(r => {
                return r.total_quantity;
            })
            .reduce((a, b) => parseInt(a) + parseInt(b), 0);
    },

    totalGrossSale(state, getters) {
        return getters.checkouts
            .map(r => {
                return r.totalGrossSales;
            })
            .reduce((a, b) => parseFloat(a) + parseFloat(b), 0);
    },

    totalGrossSales(state, getters) {
        return (
            parseFloat(getters.netSales) +
            (parseFloat(getters.modifierDiscountPerReceipt) +
                parseFloat(getters.discountPerReceipt) * -1)
        );
    },

    totalDiscountSet(state, getters) {
        return (
            (parseFloat(getters.modifierDiscountPerReceipt) +
                parseFloat(getters.discountPerReceipt) * -1) *
            -1
        );
    },

    totalSalesAfterRounding(state, getters) {
        return getters.totalSales - getters.totalRounding;
    },

    totalTaxes(state, getters) {
        /* Actual implementation
        return getters
            .nonVoidedReceipts
            .map(r => {
                return r.taxes
                    .map(t => t.amount)
                    .reduce((a, b) => (parseFloat(a) + parseFloat(b)), 0);
            })
            .reduce((a, b) => (a + b), 0);
        */

        // current workaround
        return getters.nonVoidedReceipts
            .map(r => {
                return r.transaction.raw_taxes
                    .map(t => parseFloat(t.amount))
                    .reduce((a, b) => a + b, 0);
            })
            .reduce((a, b) => a + b, 0);
    },

    taxCount(state, getters) {
        return getters.taxList.map(d => d.total).reduce((a, b) => a + b, 0);
    },

    taxList(state, getters) {
        const taxList = [];

        getters.nonVoidedReceipts.forEach(r => {
            r.transaction.raw_taxes.forEach(d => {
                taxList.push({
                    name: d.name,
                    mode: d.mode,
                    tax_amount: d.tax_amount,
                    amount: d.amount,
                });
            });
        });

        const taxs = [];

        _.forEach(_.groupBy(taxList, 'name'), (groupedTax, name) => {
            const amount = groupedTax
                .map(c => parseFloat(c.amount))
                .reduce((a, b) => a + b, 0);

            taxs.push({
                name,
                amount,
                tax_amount: groupedTax[0].tax_amount,
                total: groupedTax.length,
            });
        });

        return taxs;
    },

    totalCharges(state, getters) {
        /* Actual implementation
        return getters
            .nonVoidedReceipts
            .map(r => {
                return r.charges
                    .map(t => t.amount)
                    .reduce((a, b) => (parseFloat(a) + parseFloat(b)), 0);
            })
            .reduce((a, b) => (a + b), 0);
        */

        // current workaround
        return getters.nonVoidedReceipts
            .map(r => {
                return r.transaction.raw_charges
                    .map(t => parseFloat(t.amount))
                    .reduce((a, b) => a + b, 0);
            })
            .reduce((a, b) => a + b, 0);
    },

    totalRounding(state, getters) {
        return getters.nonVoidedReceipts
            .map(r => parseFloat(r.rounding))
            .reduce((a, b) => a + b, 0);
    },

    checkouts(state, getters) {
        const checkoutList = getters.nonVoidedReceipts.map(r => {
            let checkout = {
                order_checkout: r.transaction.order.checkout.name,
                order_product: r.transaction.order.products
                    .filter(u => u.voided_at === null)
                    .map(u => ({
                        checkout_name:
                            u.special_request == 'Takeaway'
                                ? 'Take Away'
                                : u.checkout_id != null
                                    ? u.checkout.name
                                    : r.transaction.order.checkout.name,
                        gross_sales: u.amount,
                        discount: u.discount_price,
                        unit_price: u.unit_price,
                        quantity: u.quantity,
                    })),
            };
            return checkout;
        });

        const productList = [];

        checkoutList.forEach(function(c) {
            let product = c.order_product;

            product.forEach(p => {
                productList.push({
                    checkout_name: p.checkout_name,
                    discount: p.discount,
                    gross_sales: p.gross_sales,
                    unit_price: p.unit_price,
                    quantity: p.quantity,
                });
            });
        });

        const sumProducts = [];
        let modeType = 0;

        _.forEach(
            _.groupBy(productList, 'checkout_name'),
            (groupedCheckout, name) => {
                let totalSales = groupedCheckout
                    .map(c => parseFloat(c.gross_sales))
                    .reduce((a, b) => a + b, 0);

                let discountSet = groupedCheckout
                    .map(c => parseFloat(c.discount))
                    .reduce((a, b) => a + b, 0);

                totalSales = totalSales - parseFloat(discountSet) * -1;
                modeType = groupedCheckout.length;

                const totalGrossSales = groupedCheckout
                    .map(c => parseFloat(c.unit_price) * parseInt(c.quantity))
                    .reduce((a, b) => parseFloat(a) + parseFloat(b), 0);

                sumProducts.push({
                    name,
                    totalSales,
                    totalGrossSales,
                    total: modeType,
                });
            },
        );

        return sumProducts;
    },

    /**
     * List of discounts
     * @return {Array}
     */
    discountList(state, getters) {
        const discountList = [];

        getters.nonVoidedReceipts.forEach(r => {
            r.discounts.forEach(d => {
                discountList.push({
                    name: d.name,
                    mode: d.mode,
                    amount: d.amount,
                });
            });
        });

        const discounts = [];

        _.forEach(_.groupBy(discountList, 'name'), (groupedDiscount, name) => {
            const amount = groupedDiscount
                .map(c => parseFloat(c.amount))
                .reduce((a, b) => a + b, 0);

            discounts.push({
                name,
                amount,
                total: groupedDiscount.length,
            });
        });

        return discounts;
    },

    discountPerReceipt(state, getters) {
        return getters.discountList
            .map(d => d.amount)
            .reduce((a, b) => Big(a).plus(Big(b)), 0);
    },

    modifierDiscountPerReceipt(state, getters) {
        return getters.modifierDiscountList
            .map(m => m.amount)
            .reduce((a, b) => Big(a).plus(Big(b)), 0);
    },

    modifierDiscountOnlyPerReceipt(state, getters) {
        return getters.modifierDiscountOnly
            .map(m => m.amount)
            .reduce((a, b) => Big(a).plus(Big(b)), 0);
    },

    /**
     * Return total and sum total of discount
     * @return {Object}
     */
    totalDiscount(state, getters) {
        const totalDiscount = getters.discountList
            .map(d => d.amount)
            .reduce((a, b) => Big(a).plus(Big(b)), 0);

        const totalDiscountQty = getters.discountList
            .map(d => d.total)
            .reduce((a, b) => Big(a).plus(Big(b)), 0);

        const totalModifierDiscount = getters.modifierDiscountList
            .map(m => m.amount)
            .reduce((a, b) => Big(a).plus(Big(b)), 0);

        const totalModifierDiscountOnly = getters.modifierDiscountOnly
            .map(m => m.amount)
            .reduce((a, b) => Big(a).plus(Big(b)), 0);

        const totalModifierQty = getters.modifierDiscountList
            .map(m => m.total)
            .reduce((a, b) => Big(a).plus(Big(b)), 0);

        return {
            modifier: totalModifierDiscountOnly.toFixed(2),
            amount: Big(totalDiscount)
                .abs()
                .plus(Big(totalModifierDiscount))
                .toFixed(2),
            amount_discount: Big(totalModifierDiscount).toFixed(2),
            total: Big(totalDiscountQty)
                .plus(Big(totalModifierQty))
                .toString(),
        };
    },

    modifierDiscountList(state, getters) {
        const modifierDiscounts = [];
        const modifierDiscountList = [];

        /** get actual total amount modifier - calculated from (product price + topup + deduct) * discount percentage */
        const getModifierAmount = (product, modifier) => {
            const modifierAmount = Big(modifier.amount).times(
                modifier.quantity,
            );

            // type deduct
            if (modifier.mode == 2) {
                return Big(modifierAmount).times(Big(product.quantity));
            }

            // Calculate base product price
            const productPrice = Big(product.unit_price).times(
                product.quantity,
            );

            // Calculate total topup (mode = 1) - multiply by product quantity
            const totalTopup = product.modifiers
                .filter(m => m.mode == 1)
                .reduce((sum, m) => {
                    return Big(sum).plus(
                        Big(m.amount)
                            .times(m.quantity)
                            .times(product.quantity),
                    );
                }, Big(0));

            // Calculate total deduct (mode = 2) - multiply by product quantity
            const totalDeduct = product.modifiers
                .filter(m => m.mode == 2)
                .reduce((sum, m) => {
                    return Big(sum).plus(
                        Big(m.amount)
                            .times(m.quantity)
                            .times(product.quantity),
                    );
                }, Big(0));

            // Calculate discount from (product price + topup + deduct) * discount percentage
            const baseAmount = productPrice.plus(totalTopup).plus(totalDeduct);

            // handle type discount (%)
            return modifierAmount.times(baseAmount).div(100);
        };

        getters.nonVoidedReceipts.forEach(r => {
            r.transaction.order.products
                .filter(p => p.voided_at === null)
                .forEach(p => {
                    p.modifiers
                        .filter(m => m.mode == 2 || m.mode == 3)
                        .forEach(m => {
                            modifierDiscounts.push({
                                name: m.name,
                                mode: m.mode,
                                amount: getModifierAmount(p, m),
                                quantity: m.quantity,
                            });
                        });
                });
        });

        _.forEach(
            _.groupBy(modifierDiscounts, 'name'),
            (groupedDiscount, name) => {
                const amount = groupedDiscount
                    .map(m => m.amount)
                    .reduce((a, b) => Big(a).plus(b), 0)
                    .toFixed(2);

                const total = groupedDiscount
                    .map(m => parseInt(m.quantity))
                    .reduce((a, b) => a + b, 0)
                    .toString();

                modifierDiscountList.push({
                    name,
                    amount,
                    total,
                });
            },
        );

        return modifierDiscountList;
    },

    modifierDiscountOnly(state, getters) {
        const modifierDiscounts = [];
        const modifierDiscountList = [];

        /** get actual total amount modifier - only returns the discount percentage value */
        const getModifierAmount = (product, modifier) => {
            const modifierAmount = Big(modifier.amount).times(
                modifier.quantity,
            );

            // type deduct
            if (modifier.mode == 2) {
                return Big(modifierAmount).times(Big(product.quantity));
            }

            // handle type discount (%) - return only the percentage value
            return modifierAmount;
        };

        getters.nonVoidedReceipts.forEach(r => {
            r.transaction.order.products
                .filter(p => p.voided_at === null)
                .forEach(p => {
                    p.modifiers
                        .filter(m => m.mode == 2 || m.mode == 3)
                        .forEach(m => {
                            modifierDiscounts.push({
                                name: m.name,
                                mode: m.mode,
                                amount: getModifierAmount(p, m),
                                quantity: m.quantity,
                            });
                        });
                });
        });

        _.forEach(
            _.groupBy(modifierDiscounts, 'name'),
            (groupedDiscount, name) => {
                const amount = groupedDiscount
                    .map(m => m.amount)
                    .reduce((a, b) => Big(a).plus(b), 0)
                    .toFixed(2);

                const total = groupedDiscount
                    .map(m => parseInt(m.quantity))
                    .reduce((a, b) => a + b, 0)
                    .toString();

                modifierDiscountList.push({
                    name,
                    amount,
                    total,
                });
            },
        );

        return modifierDiscountList;
    },

    modifierTopupList(state, getters) {
        const modifierTopups = [];
        const modifierTopupList = [];

        const getModifierAmount = (product, modifier) => {
            const modifierAmount = Big(modifier.amount).times(
                modifier.quantity,
            );

            // type topup
            if (modifier.mode == 1) {
                // let ts = modifierAmount
                //     .times(getters.totalDiscount.modifier)
                //     .div(100);
                // return modifierAmount.minus(ts);
                return Big(modifierAmount).times(Big(product.quantity));
            }

            return Big(0);
        };

        getters.nonVoidedReceipts.forEach(r => {
            r.transaction.order.products
                .filter(p => p.voided_at === null)
                .forEach(p => {
                    p.modifiers.filter(m => m.mode == 1).forEach(m => {
                        modifierTopups.push({
                            name: m.name,
                            mode: m.mode,
                            amount: getModifierAmount(p, m),
                            quantity: m.quantity,
                        });
                    });
                });
        });

        _.forEach(_.groupBy(modifierTopups, 'name'), (groupedTopup, name) => {
            const amount = groupedTopup
                .map(m => m.amount)
                .reduce((a, b) => Big(a).plus(b), 0)
                .toFixed(2);

            const total = groupedTopup
                .map(m => parseInt(m.quantity))
                .reduce((a, b) => a + b, 0)
                .toString();

            modifierTopupList.push({
                name,
                amount,
                total,
            });
        });

        return modifierTopupList;
    },

    totalAddon(state, getters) {
        const totalAddon = getters.modifierTopupList
            .map(d => d.amount)
            .reduce((a, b) => Big(a).plus(Big(b)), 0);

        const totalAddonQty = getters.modifierTopupList
            .map(d => d.total)
            .reduce((a, b) => Big(a).plus(Big(b)), 0);

        return {
            amount: Big(totalAddon).toFixed(2),
            total: Big(totalAddonQty).toString(),
        };
    },

    totalTopup(state, getters) {
        return getters.modifierTopupList
            .map(d => d.amount)
            .reduce((a, b) => parseFloat(a) + parseFloat(b), 0);
    },

    totalPayment(state, getters) {
        const totalPayment = getters.paymentLists
            .map(d => d.amount)
            .reduce((a, b) => Big(a).plus(Big(b)), 0);

        const totalPaymentQty = getters.paymentLists
            .map(d => d.total)
            .reduce((a, b) => Big(a).plus(Big(b)), 0);

        return {
            amount: Big(totalPayment).toFixed(2),
            total: Big(totalPaymentQty).toString(),
        };
    },

    /**
     * @return {Object}
     */
    payments(state, getters) {
        const paymentList = [];
        const payments = {};

        const getTypeName = type => {
            if (type == 0) return 'Cash';
            if (type == 1) return 'Credit Card';
            if (type == 2) return 'Cheque';
            if (type == 3) return 'Voucher';
            if (type == 4) return 'Others';
        };

        getters.nonVoidedReceipts.forEach(r => {
            r.payments.forEach(p => {
                paymentList.push({
                    name: p.payment.name,
                    type: getTypeName(p.payment.type),
                    amount:
                        parseFloat(p.amount) -
                        (p.change === null ? 0 : parseFloat(p.change)),
                });
            });
        });

        // loop grouped by type
        _.forEach(
            _.groupBy(paymentList, 'type'),
            (groupedPayment, typeName) => {
                const groupedPaymentByName = _.groupBy(groupedPayment, 'name');
                let typeSummary = {
                    amount: 0,
                    total: 0,
                };

                payments[typeName] = [];

                // loop grouped by payment name
                _.forEach(groupedPaymentByName, (paymentListByName, name) => {
                    const amount = paymentListByName
                        .map(p => parseFloat(p.amount))
                        .reduce((a, b) => a + b, 0);

                    payments[typeName].push({
                        name,
                        amount,
                        total: paymentListByName.length,
                    });

                    typeSummary.amount += amount;
                    typeSummary.total += paymentListByName.length;
                });

                // sum total by type
                payments[typeName].push(
                    Object.assign({ name: 'Total' }, typeSummary),
                );
            },
        );

        return payments;
    },

    paymentLists(state, getters) {
        const paymentList = [];
        const payments = {};

        const getTypeName = type => {
            if (type == 0) return 'Cash';
            if (type == 1) return 'Credit Card';
            if (type == 2) return 'Cheque';
            if (type == 3) return 'Voucher';
            if (type == 4) return 'Others';
        };

        getters.nonVoidedReceipts.forEach(r => {
            r.payments.forEach(p => {
                paymentList.push({
                    name: p.payment.name,
                    type: getTypeName(p.payment.type),
                    amount:
                        parseFloat(p.amount) -
                        (p.change === null ? 0 : parseFloat(p.change)),
                });
            });
        });

        // loop grouped by type
        _.forEach(
            _.groupBy(paymentList, 'type'),
            (groupedPayment, typeName) => {
                const groupedPaymentByName = _.groupBy(groupedPayment, 'name');
                let typeSummary = {
                    amount: 0,
                    total: 0,
                };

                payments[typeName] = [];

                // loop grouped by payment name
                _.forEach(groupedPaymentByName, (paymentListByName, name) => {
                    const amount = paymentListByName
                        .map(p => parseFloat(p.amount))
                        .reduce((a, b) => a + b, 0);

                    payments[typeName].push({
                        name,
                        amount,
                        total: paymentListByName.length,
                    });
                });
            },
        );

        const result = _.flatMap(payments, function(n) {
            return n;
        });

        return result;
    },

    cashIn(state) {
        const cashIn = state.drawerSession.cash_adjustments.filter(
            c => c.type == 0,
        );

        const amount = cashIn
            .map(c => parseFloat(c.amount))
            .reduce((a, b) => a + b, 0);

        return {
            total: cashIn.length,
            amount,
        };
    },

    cashOut(state) {
        const cashOut = state.drawerSession.cash_adjustments.filter(
            c => c.type == 1,
        );

        const amount = cashOut
            .map(c => parseFloat(c.amount))
            .reduce((a, b) => a + b, 0);

        return {
            total: cashOut.length,
            amount,
        };
    },

    cashSummary(state, getters) {
        const payments = getters.payments;
        let cashSummary = {
            total: 0,
            amount: 0,
        };

        if (payments.hasOwnProperty('Cash')) {
            let cash = payments['Cash'].find(p => p.name === 'Total');

            if (!_.isUndefined(cash)) cashSummary = cash;
        }

        return cashSummary;
    },

    totalTransaction(state, getters) {
        return getters.nonVoidedReceipts.length;
    },

    expectedDrawerAmount(state, getters) {
        const openingAmount = Big(state.drawerSession.opening_amount);
        const totalCash = Big(getters.cashSummary.amount);
        const cashIn = Big(getters.cashIn.amount);
        const cashOut = Big(getters.cashOut.amount);
        const expectedDrawerAmount = totalCash
            .plus(openingAmount)
            .plus(cashIn)
            .minus(cashOut);

        return expectedDrawerAmount.toFixed(2);
    },

    shortOrExtra(state, getters) {
        const closingAmount = Big(state.drawerSession.closing_amount);

        return closingAmount
            .minus(Big(getters.expectedDrawerAmount))
            .toFixed(2);
    },
};

const actions = {
    getDrawerSession({ commit, dispatch }, id) {
        let route = Laroute.route('franchise.report.drawersession.show', {
            id,
        });
        startLoading(dispatch, 'fetch-drawer-session');

        return axios
            .get(route)
            .then(res => {
                endLoading(dispatch, 'fetch-drawer-session');
                commit(type.SET_DRAWER_SESSION, res.data);
                return res;
            })
            .catch(err => {
                endLoading(dispatch, 'fetch-drawer-session');
                throw err;
            });
    },
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};



// WEBPACK FOOTER //
// ./resources/assets/js/store/modules/drawer-session.js